import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { defineModal } from '@/lib/modals';
import { useForm, useStore } from '@tanstack/react-form';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExecutorProfileSelector } from '@/components/settings';
import RepoBranchSelector from '@/components/tasks/RepoBranchSelector';
import { useUserSystem } from '@/components/ConfigProvider';
import {
  useTaskMutations,
  useProjectRepos,
  useRepoBranchSelection,
} from '@/hooks';
import {
  generateSystemInstruction,
  type RunningMode,
} from '@/lib/runSelectedTasksInstruction';
import type { TaskWithAttemptStatus, ExecutorProfileId } from 'shared/types';

export type RunSelectedTasksDialogProps = {
  projectId: string;
  todoTasks: TaskWithAttemptStatus[];
};

type FormValues = {
  title: string;
  description: string;
  selectedTaskIds: string[];
  runningMode: RunningMode;
  executorProfileId: ExecutorProfileId | null;
};

const RunSelectedTasksDialogImpl =
  NiceModal.create<RunSelectedTasksDialogProps>(({ projectId, todoTasks }) => {
    const modal = useModal();
    const { t } = useTranslation(['tasks', 'common']);
    const { system, profiles } = useUserSystem();
    const { createAndStart } = useTaskMutations(projectId);

    const { data: projectRepos = [] } = useProjectRepos(projectId, {
      enabled: modal.visible,
    });

    const {
      configs: repoBranchConfigs,
      isLoading: branchesLoading,
      selectedRepoIds,
      toggleRepo,
      selectAll,
      selectNone,
      getWorkspaceRepoInputs,
      setRepoBranch,
    } = useRepoBranchSelection({
      repos: projectRepos,
      enabled: modal.visible && projectRepos.length > 0,
    });

    const defaultValues = useMemo<FormValues>(
      () => ({
        title: '',
        description: '',
        selectedTaskIds: [],
        runningMode: 'sequential',
        executorProfileId: system.config?.executor_profile || null,
      }),
      [system.config?.executor_profile]
    );

    const handleSubmit = async ({ value }: { value: FormValues }) => {
      if (!value.executorProfileId) return;

      const systemInstruction = generateSystemInstruction({
        runningMode: value.runningMode,
        subtaskIds: value.selectedTaskIds,
        userDescription: value.description,
      });

      const task = {
        project_id: projectId,
        title: value.title,
        description: systemInstruction,
        status: null,
        parent_workspace_id: null,
        image_ids: null,
        assigned_user_id: null,
      };

      const repos = getWorkspaceRepoInputs();

      // Prevent submission if no repos selected
      if (repos.length === 0) {
        return;
      }

      await createAndStart.mutateAsync(
        {
          task,
          executor_profile_id: value.executorProfileId,
          repos,
        },
        { onSuccess: () => modal.remove() }
      );
    };

    const validator = (value: FormValues): string | undefined => {
      if (!value.title.trim().length) return 'Title is required';
      if (value.selectedTaskIds.length < 2) return 'Select at least 2 tasks';
      if (!value.executorProfileId) return 'Select an executor profile';
    };

    const form = useForm({
      defaultValues,
      onSubmit: handleSubmit,
      validators: {
        onMount: ({ value }) => validator(value),
        onChange: ({ value }) => validator(value),
      },
    });

    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    const canSubmit = useStore(form.store, (state) => state.canSubmit);

    // Check if a task is already linked to another parent (cannot be selected)
    const isTaskLinked = useCallback(
      (task: {
        parent_workspace_id: string | null;
        task_list_id: string | null;
      }) => {
        return task.parent_workspace_id != null || task.task_list_id != null;
      },
      []
    );

    // Filter available tasks (not linked to any parent)
    const availableTasks = useMemo(
      () => todoTasks.filter((task) => !isTaskLinked(task)),
      [todoTasks, isTaskLinked]
    );

    const toggleTask = useCallback(
      (taskId: string, checked: boolean) => {
        form.setFieldValue('selectedTaskIds', (prev) =>
          checked ? [...prev, taskId] : prev.filter((id) => id !== taskId)
        );
      },
      [form]
    );

    const handleSelectAll = useCallback(() => {
      // Only select available (non-linked) tasks
      const availableIds = availableTasks.map((t) => t.id);
      form.setFieldValue('selectedTaskIds', availableIds);
    }, [form, availableTasks]);

    const handleDeselectAll = useCallback(() => {
      form.setFieldValue('selectedTaskIds', []);
    }, [form]);

    if (branchesLoading) {
      return null;
    }

    return (
      <Dialog
        open={modal.visible}
        onOpenChange={(open) => !open && modal.remove()}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('runSelectedTasks.title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Parent Task Title */}
            <form.Field name="title">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="parent-title">
                    {t('runSelectedTasks.parentTitle')}
                  </Label>
                  <Input
                    id="parent-title"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('runSelectedTasks.parentTitlePlaceholder')}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </form.Field>

            {/* Parent Task Description */}
            <form.Field name="description">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="parent-description">
                    {t('runSelectedTasks.parentDescription')}
                  </Label>
                  <Input
                    id="parent-description"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t(
                      'runSelectedTasks.parentDescriptionPlaceholder'
                    )}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </form.Field>

            {/* Running Mode Selector */}
            <form.Field name="runningMode">
              {(field) => (
                <div className="space-y-2">
                  <Label>{t('runSelectedTasks.runningMode')}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) =>
                      field.handleChange(value as RunningMode)
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sequential">
                        {t('runSelectedTasks.modes.sequential')}
                      </SelectItem>
                      <SelectItem value="ai-sequential">
                        {t('runSelectedTasks.modes.aiSequential')}
                      </SelectItem>
                      <SelectItem value="ai-parallel">
                        {t('runSelectedTasks.modes.aiParallel')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            {/* Repo/Branch Selector */}
            <div className="space-y-2">
              <Label>{t('runSelectedTasks.selectRepos')}</Label>
              <RepoBranchSelector
                configs={repoBranchConfigs}
                selectedRepoIds={selectedRepoIds}
                onRepoToggle={toggleRepo}
                onSelectAll={selectAll}
                onSelectNone={selectNone}
                onBranchChange={setRepoBranch}
                isLoading={branchesLoading}
              />
            </div>

            {/* Executor Profile Selector */}
            <form.Field name="executorProfileId">
              {(field) => (
                <div className="space-y-2">
                  <Label>{t('runSelectedTasks.executorProfile')}</Label>
                  <ExecutorProfileSelector
                    profiles={profiles}
                    selectedProfile={field.state.value}
                    onProfileSelect={(profile) => field.handleChange(profile)}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </form.Field>

            {/* Task Multi-Select */}
            <form.Field name="selectedTaskIds">
              {(field) => (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t('runSelectedTasks.selectTasks')}</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                        disabled={isSubmitting}
                      >
                        {t('runSelectedTasks.selectAll')}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleDeselectAll}
                        disabled={isSubmitting}
                      >
                        {t('runSelectedTasks.deselectAll')}
                      </Button>
                    </div>
                  </div>
                  <div className="h-48 border rounded-md overflow-y-auto">
                    <div className="p-2 space-y-2">
                      {todoTasks.map((task) => {
                        const isLinked = isTaskLinked(task);
                        return (
                          <div
                            key={task.id}
                            className={`flex items-center gap-2 p-2 rounded ${
                              isLinked
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-muted cursor-pointer'
                            }`}
                            title={
                              isLinked
                                ? t('runSelectedTasks.taskAlreadyLinked')
                                : undefined
                            }
                          >
                            <Checkbox
                              id={`task-${task.id}`}
                              checked={field.state.value.includes(task.id)}
                              onCheckedChange={(checked) =>
                                toggleTask(task.id, checked === true)
                              }
                              disabled={isSubmitting || isLinked}
                            />
                            <label
                              htmlFor={isLinked ? undefined : `task-${task.id}`}
                              className={`text-sm flex-1 ${isLinked ? '' : 'cursor-pointer'}`}
                            >
                              {task.title}
                            </label>
                            {isLinked && (
                              <span className="text-xs text-muted-foreground">
                                {t('runSelectedTasks.linked')}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {todoTasks.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {t('runSelectedTasks.noTasks')}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('runSelectedTasks.selectedCount', {
                      count: field.state.value.length,
                    })}
                  </p>
                </div>
              )}
            </form.Field>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => modal.remove()}
              disabled={isSubmitting}
            >
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={form.handleSubmit}
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting
                ? t('runSelectedTasks.creating')
                : t('runSelectedTasks.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  });

export const RunSelectedTasksDialog = defineModal<
  RunSelectedTasksDialogProps,
  void
>(RunSelectedTasksDialogImpl);
